import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress } from '@mui/material';
import {
  ArrowForwardIosRounded,
  MicRounded,
  StopRounded,
  VolumeUpRounded,
} from '@mui/icons-material';
import actions from '@src/redux/actions';
import {
  MIN_AUDIO_DURATION,
  NEW_AUDIO_STATUS,
  AUDIO_TYPE,
} from '@src/constants/voiceCloning';
import { CUSTOMER_SUPPORT_PHONE_NUMBER } from '@src/configs';
import { uploadAudioLinkToS3 } from '@src/services/upload';
import apis from '@src/apis';
import useRecording from '@src/hooks/useRecording';
import { StyledRecordingActions } from './index.style';
import { requestMicrophonePermission } from '../../../services/microphone';

const ActionRecordButton = ({
  icon,
  text,
  loadingUpdateAudio,
  recordBtnDisabled,
  isHighlightRecordButton,
  onClickButton,
}) => (
  <Button
    className={`action-button ${
      isHighlightRecordButton ? 'highlight-button' : ''
    }`}
    disabled={recordBtnDisabled || loadingUpdateAudio}
    onClick={onClickButton}
  >
    <Box className={`record-box ${recordBtnDisabled ? 'disabled' : ''}`}>
      {icon}
      {text}
    </Box>
  </Button>
);

const RehearButton = ({
  isRehearing,
  isRecording,
  rechearBtnDisabled,
  toggleRehearing,
}) => {
  const { t } = useTranslation();

  return (
    <Button
      onClick={toggleRehearing}
      className="rehear-button action-button pending-button"
      disabled={rechearBtnDisabled}
    >
      {isRehearing ? (
        <Box className={`rehear-box ${isRecording ? 'disabled' : ''}`}>
          <StopRounded className="rehear-icon" />
          {t('stop')}
        </Box>
      ) : (
        <Box className={`rehear-box ${rechearBtnDisabled ? 'disabled' : ''}`}>
          <VolumeUpRounded className="rehear-icon" />
          {t('rehear')}
        </Box>
      )}
    </Button>
  );
};

const RecordButton = ({
  isRecording,
  isHighlightRecordButton,
  recordBtnDisabled,
  loadingUpdateAudio,
  toggleRecording,
}) => {
  const { t } = useTranslation();

  if (loadingUpdateAudio)
    return (
      <ActionRecordButton
        icon={<CircularProgress size={25} className="progress" />}
        text={t('processing')}
        isHighlightRecordButton={isHighlightRecordButton}
        recordBtnDisabled={recordBtnDisabled}
        loadingUpdateAudio={loadingUpdateAudio}
      />
    );

  if (isRecording)
    return (
      <ActionRecordButton
        icon={<StopRounded className="record-icon" />}
        text={t('stop')}
        onClickButton={toggleRecording}
        isHighlightRecordButton={isHighlightRecordButton}
        recordBtnDisabled={recordBtnDisabled}
        loadingUpdateAudio={loadingUpdateAudio}
      />
    );

  return (
    <ActionRecordButton
      icon={<MicRounded className="record-icon" />}
      text={t('voiceRecord')}
      onClickButton={toggleRecording}
      isHighlightRecordButton={isHighlightRecordButton}
      recordBtnDisabled={recordBtnDisabled}
      loadingUpdateAudio={loadingUpdateAudio}
    />
  );
};

const NextButton = ({
  sentences,
  currentIndex,
  nextSentenceDisabled,
  isHighlightNextButton,
  handleNextSentence,
}) => {
  const { t } = useTranslation();
  return (
    <Button
      className={`next-sentence-button action-button pending-button ${
        isHighlightNextButton ? 'highlight-button' : ''
      }`}
      onClick={handleNextSentence}
      disabled={nextSentenceDisabled}
    >
      <Box
        className={`next-sentence-box ${
          nextSentenceDisabled ? 'disabled' : ''
        }`}
      >
        <ArrowForwardIosRounded className="next-sentence-icon" />
        {currentIndex === sentences.length - 1
          ? t('finished')
          : t('nextSentence')}
      </Box>
    </Button>
  );
};

const RecordingActions = ({
  audios,
  sentences,
  currentIndex,
  isRecording,
  loadingUpdateAudio,
  audioToPlay,
  isRehearing,
  isVCNewRecording,
  handleNextSentence,
  setIsRecording,
  setAudioDurationFail,
  setLoadingUpdateAudio,
  setIsRehearing,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { voiceId } = useParams();
  const {
    isRecording: recordingStatus,
    interruptedAudioLink,
    interruptedAudioDuration,
    startRecording,
    stopRecording,
    setContentRecord,
    newStartRecording,
    newStopRecording,
    setRecordingMethod,
  } = useRecording();

  const rechearBtnDisabled =
    isRecording ||
    loadingUpdateAudio ||
    !audioToPlay ||
    audios[currentIndex].status === NEW_AUDIO_STATUS.FAILED;
  const recordBtnDisabled = isRehearing || loadingUpdateAudio;
  const nextSentenceDisabled =
    isRecording ||
    isRehearing ||
    loadingUpdateAudio ||
    !audioToPlay ||
    audios[currentIndex].status === NEW_AUDIO_STATUS.FAILED;

  const isHighlightRecordButton =
    !audioToPlay ||
    audios[currentIndex].status === NEW_AUDIO_STATUS.FAILED ||
    !Object.values(audios[currentIndex].quality).every(Boolean);
  const isHighlightNextButton =
    !isHighlightRecordButton &&
    (!nextSentenceDisabled ||
      (audios[currentIndex]?.status &&
        audios[currentIndex].status !== NEW_AUDIO_STATUS.FAILED));

  const showToastNoti = (severity, message) =>
    dispatch(actions.noti.push({ severity, message }));

  const toggleRehearing = () => setIsRehearing((prev) => !prev);

  const updateAudiosArray = (
    audiosArray,
    audioId,
    newAudioLink,
    newQuality,
    newStatus,
  ) =>
    audiosArray.map((audio) => {
      if (audio.id === audioId) {
        return {
          ...audio,
          audioLink: newAudioLink,
          quality: newQuality,
          status: newStatus,
        };
      }
      return audio;
    });

  const handleUploadAudioToS3 = async (file) => {
    const extension = AUDIO_TYPE.WAV;
    const currentAudioCode = audios[currentIndex]?.sentence?.code;
    const fileName = currentAudioCode;
    const directory = `voice-cloning/voices/${voiceId}`;
    const { error, url: s3Url } = await uploadAudioLinkToS3(
      fileName,
      directory,
      extension,
      file,
    );

    if (error) return null;
    return s3Url;
  };

  const handleUpdateAudio = async (link) => {
    setLoadingUpdateAudio(true);
    const audioS3Url = await handleUploadAudioToS3(link);
    if (!audioS3Url) {
      const message = t('systemError', {
        hotline: CUSTOMER_SUPPORT_PHONE_NUMBER,
      });
      showToastNoti('error', message);
      setLoadingUpdateAudio(false);
      return;
    }

    const audioId = audios[currentIndex]?.id;
    const response = await apis.voiceCloning.updateVoiceCloningAudio(audioId, {
      audioLink: audioS3Url,
    });
    if (!response?.status) {
      showToastNoti('error', 'createAudioFailed');
      setLoadingUpdateAudio(false);
      return;
    }
    showToastNoti('success', 'theAudioIsSavedSuccessfully!');

    const audioCurrent = response?.result?.audio;
    const updatedAudios = updateAudiosArray(
      audios,
      audioId,
      audioCurrent.audioLink,
      audioCurrent.quality,
      audioCurrent.status,
    );
    dispatch(actions.audioVoiceCloning.updateAudios(updatedAudios));

    setLoadingUpdateAudio(false);
  };

  const processStopRecording = async (recordTime, audioLink) => {
    if (!audioLink || !recordTime) {
      showToastNoti('error', 'createAudioFailed');
      return;
    }

    if (recordTime < MIN_AUDIO_DURATION) {
      setAudioDurationFail(true);
      showToastNoti('error', 'audioDurationTooShort');
    } else {
      setAudioDurationFail(false);
      await handleUpdateAudio(audioLink);
    }
  };

  const stopRecordingRecursive = async () => {
    const { recordTime, audioLink } = isVCNewRecording
      ? await newStopRecording()
      : await stopRecording();
    await processStopRecording(recordTime, audioLink);
  };

  const toggleRecording = async () => {
    const hasPermission = await requestMicrophonePermission();
    if (!hasPermission) {
      showToastNoti('error', 'microphonePermissionDenied');
      return;
    }

    const currentState = isRecording;

    if (currentState) await stopRecordingRecursive();
    else if (isVCNewRecording) await newStartRecording();
    else await startRecording();
  };

  useEffect(() => {
    if (sentences[currentIndex].originalText.trim())
      setContentRecord(sentences[currentIndex].originalText.trim());
  }, [sentences]);

  useEffect(() => {
    setIsRecording(recordingStatus);
  }, [recordingStatus]);

  useEffect(() => {
    if (interruptedAudioLink && interruptedAudioDuration)
      processStopRecording(interruptedAudioDuration, interruptedAudioLink);
  }, [interruptedAudioLink, interruptedAudioDuration]);

  useEffect(() => {
    setRecordingMethod(isVCNewRecording);
  }, [isVCNewRecording]);

  return (
    <StyledRecordingActions>
      <RehearButton
        isRecording={isRecording}
        isRehearing={isRehearing}
        toggleRehearing={toggleRehearing}
        rechearBtnDisabled={rechearBtnDisabled}
      />
      <RecordButton
        loadingUpdateAudio={loadingUpdateAudio}
        isRecording={isRecording}
        toggleRecording={toggleRecording}
        isHighlightRecordButton={isHighlightRecordButton}
        recordBtnDisabled={recordBtnDisabled}
      />
      <NextButton
        isHighlightNextButton={isHighlightNextButton}
        handleNextSentence={handleNextSentence}
        nextSentenceDisabled={nextSentenceDisabled}
        currentIndex={currentIndex}
        sentences={sentences}
      />
    </StyledRecordingActions>
  );
};

export default RecordingActions;
